<template>
    <div class="flex flex-col border border-solid border-gray-300 bg-white rounded-xl mb-2">
        <slot name="purchase-block-header-image"></slot>

        <div class="px-6 py-2 bg-blue-600 font-bold mb-2 tex-center text-center text-white">
            <template v-if="!hasEnded">
                Presale ends in
                <template v-if="weeks > 0">{{ weeks }} weeks</template>
                <template v-if="days > 0">{{ days }} days</template>
                <template v-if="hours > 0">{{ hours }} hrs</template>
                <template v-if="minutes > 0 && weeks < 1 && days < 1">{{ minutes }} mins</template>
                <template v-if="seconds > 0 && weeks < 1 && days < 1 && hours < 1">{{ seconds }}s</template>
            </template>
            <template v-else>
                Presale has finished
            </template>
        </div>
        <div class="px-6 pt-2 font-bold">
            <a :href="eventSchedule.url" target="_blank">
                {{ event.title }}
            </a>
        </div>
        <div class="px-6">
            <template v-if="!eventSchedule.date_end_formatted">
                {{ eventSchedule.date_formatted }}
            </template>
            <template v-else> {{ eventSchedule.date_formatted }} - {{ eventSchedule.date_end_formatted }} </template>
        </div>
        <div class="border border-solid border-gray-300 rounded-xl mt-2 mx-6 pt-1 pb-2 px-4">
            <div class="pt-2 text-sm">
                Your Season Ticket
            </div>
            <div class="pt-0 font-bold text-lg">
                {{ seasonTicket.season_ticket_type.name }}
            </div>
            <div class="" v-if="seasonTicket.order_line.metadata && seasonTicket.order_line.metadata">
                {{ seasonTicket.order_line.metadata.name }}
            </div>
            <div v-if="seasonTicket.order_line.seat">
                <span class="font-bold">{{ seasonTicket.order_line.seatedTicketInfo.block }}</span>
                <span class="lowercase">{{ seasonTicket.order_line.seatedTicketInfo.aka_row }}</span>
                <span class="font-bold">{{ seasonTicket.order_line.seatedTicketInfo.row }}</span> seat
                <span class="font-bold">{{ seasonTicket.order_line.seatedTicketInfo.seat }}</span>
            </div>
        </div>
        <div class="px-6 pt-4" v-if="!orderLine || !orderLine.completed_at">
            <div class="">Please choose your ticket:</div>
            <div style="font-weight:bold;">
                Tickets are for collection only. Please contact office@barrowafc.com if you need your tickets delivered.
            </div>
            <div class="max-h-60 overflow-y-auto">
                <template v-if="seasonTicket.order_line.seat && presale.type != 'standard_only'">
                    <template v-for="(variation, index) in eventSchedule.variable_seat_pricing_config">
                        <div
                            v-if="!variation.hide_from_frontend"
                            @click="onSelectTicketType(variation)"
                            class="mt-3 py-2 px-6 w-full rounded-md font-medium cursor-pointer flex hover:bg-blue-400 hover:text-white"
                            :class="{
                                'bg-gray-100': !selectedTicketType || selectedTicketType.name !== variation.name,
                                'text-white bg-blue-500 hover:bg-blue-500':
                                    selectedTicketType && selectedTicketType.name === variation.name
                            }"
                        >
                            <div class="flex-1">
                                {{ variation.name }}
                            </div>
                            <div class="flex"><span v-html="currency.symbol"></span>{{ variation.price }}</div>
                        </div>
                    </template>
                </template>
                <template v-else>
                    <template v-for="(variation, index) in eventSchedule.ticket_variants">
                        <div
                            v-if="!variation.hide_from_frontend"
                            @click="onSelectTicketType(variation)"
                            class="mt-3 py-2 px-6 w-full rounded-md font-medium cursor-pointer flex hover:bg-blue-400 hover:text-white"
                            :class="{
                                'bg-gray-100': !selectedTicketType || selectedTicketType.type !== variation.type,
                                'text-white bg-blue-500 hover:bg-blue-500':
                                    selectedTicketType && selectedTicketType.type === variation.type
                            }"
                        >
                            <div class="flex-1">
                                {{ variation.type }}
                            </div>
                            <div class="flex"><span v-html="currency.symbol"></span>{{ variation.price_human }}</div>
                        </div>
                    </template>
                </template>
            </div>
        </div>
        <slot name="purchase-block-header"></slot>
        <div class="flex-1"></div>
        <div class="px-6 pb-6 pt-4">
            <hr class="mt-4 mb-3" />
            <slot name="purchase-block-footer"></slot>

            <template v-if="orderLine && orderLine.completed_at">
                <div class="flex items-center">
                    <div class="mr-2">Purchased</div>
                    <i class="text-2xl fa fa-check-circle text-green"></i>
                    <a
                        :href="'/account/order/' + orderLine.order_id + '/line/' + orderLine.id + '/ticket/pdf'"
                        class="text-white w-full text-center ml-4 px-6 py-2 rounded-md font-medium bg-blue-600 hover:bg-blue-800"
                        target="_blank"
                    >
                        Get ticket
                    </a>
                </div>
            </template>
            <template v-else>
                <button
                    @click="onClickBuyTicket"
                    class="mt-3 text-white py-2 px-6 w-full rounded-md font-medium"
                    :class="{
                        'bg-gray-400 cursor-default': hasEnded || (!orderLine && !selectedTicketType),
                        'bg-blue-600 hover:bg-blue-800': !hasEnded && (orderLine || selectedTicketType)
                    }"
                    :disabled="hasEnded || (!orderLine && !selectedTicketType)"
                >
                    Buy Ticket
                </button>
            </template>
        </div>
    </div>
</template>

<script>
import moment from "moment-timezone";

export default {
    props: {
        presale: {
            type: Object,
            required: false
        },
        event: {
            type: Object,
            required: true
        },
        eventSchedule: {
            type: Object,
            required: true
        },
        seasonTicket: {
            type: Object,
            required: false
        },
        orderLine: {
            type: Object,
            required: false
        }
    },
    data: function() {
        return {
            website: window.website,
            currency: window.currency,
            loading: true,
            selectedTicketType: null,
            endDate: moment(this.presale.end_date).tz(this.$website.timezone, true),
            weeks: 0,
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
            hasEnded: false,
            countdownIntervalId: null
        };
    },
    computed: {
        totalBookingFee: function() {
            if (this.selectedTicketType) {
                if (this.selectedTicketType.booking_fee_basis == 0) {
                    return parseFloat(this.selectedTicketType.booking_fee / 100) * this.totalTicketCost;
                } else if (this.selectedTicketType.booking_fee_basis == 1) {
                    return this.selectedTicketType.booking_fee * this.selectedQuantity;
                }
            }
            return 0;
        },
        subtotal: function() {
            if (this.selectedTicketType) {
                return parseFloat(this.selectedTicketType.price);
            }
            return 0;
        }
    },
    mounted() {
        this.updateCountdown();
        this.countdownIntervalId = setInterval(this.updateCountdown, 1000);
    },
    methods: {
        updateCountdown() {
            const currentDate = moment();
            const diff = moment(this.endDate)
                .tz(this.$website.timezone)
                .diff(currentDate);
            const duration = moment.duration(diff);
            this.weeks = parseInt(duration.asWeeks());
            this.days = duration.days();
            this.hours = duration.hours();
            this.minutes = duration.minutes();
            this.seconds = duration.seconds();
            this.hasEnded = this.seconds < 0 || this.minutes < 0 || this.hours < 0;
            if (this.hasEnded) {
                clearInterval(this.countdownIntervalId);
            }
        },
        onSelectTicketType(variation) {
            this.selectedTicketType = variation;
        },
        async onClickBuyTicket() {
            let response = await axios.post(
                "/account/presale/purchase/" + this.presale.id + "/" + this.seasonTicket.id,
                {
                    ticket_type: this.selectedTicketType.name ?? this.selectedTicketType.type
                }
            );
            if (response.status === 200) {
                window.location = "/account/order/" + response.data.id;
            } else {
                this.$toasted.show("There was a problem purchasing your ticket.", {
                    theme: "bubble",
                    type: "error",
                    position: "top-center",
                    duration: 3000
                });
            }
        }
    },
    beforeDestroy() {
        clearInterval(this.countdownIntervalId);
    }
};
</script>



// WEBPACK FOOTER //
// resources/js/components/presale-ticket-card.vue